import { Component, type ErrorInfo, type ReactNode } from "react";
import { Sentry } from "@/lib/sentry";

type Props = {
  /** Identifier reported alongside the error so we can tell which surface threw. */
  scope?: string;
  /** Optional custom fallback. When omitted, the default panel is used. */
  fallback?: (args: { error: unknown; reset: () => void; scope?: string }) => ReactNode;
  children: ReactNode;
};

type State = { error: unknown | null };

/**
 * Per-surface React error boundary. Catches a render/lifecycle throw inside
 * `children`, reports it to Sentry (no-op when DSN is unset) + the console,
 * and renders a friendly recovery panel instead of unmounting the whole tree.
 *
 * Wrap top-level routes with this so a single page regression no longer
 * blanks the entire app shell — the sidebar, banners, and notification
 * center stay alive while the page area shows the fallback.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    try {
      Sentry.withScope((scope) => {
        scope.setTag("error_boundary", this.props.scope ?? "route");
        if (info?.componentStack) {
          scope.setExtra("componentStack", info.componentStack);
        }
        Sentry.captureException(error);
      });
    } catch {
      // ignore — Sentry is best-effort
    }
    // eslint-disable-next-line no-console
    console.error(
      `[AppErrorBoundary${this.props.scope ? `:${this.props.scope}` : ""}]`,
      error,
      info?.componentStack,
    );
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error !== null) {
      if (this.props.fallback) {
        return this.props.fallback({ error: this.state.error, reset: this.reset, scope: this.props.scope });
      }
      return <DefaultRouteFallback error={this.state.error} reset={this.reset} scope={this.props.scope} />;
    }
    return this.props.children;
  }
}

function DefaultRouteFallback({
  error,
  reset,
  scope,
}: {
  error: unknown;
  reset: () => void;
  scope?: string;
}) {
  const message = (error as Error)?.message ?? String(error);
  return (
    <div
      role="alert"
      className="flex flex-1 items-center justify-center p-6"
      data-testid="route-error-boundary"
    >
      <div className="max-w-md w-full rounded-lg border border-border bg-card p-6 text-center shadow-sm">
        <h2 className="text-lg font-semibold text-foreground mb-2">Something went wrong</h2>
        <p className="text-sm text-muted-foreground mb-4">
          This page hit an unexpected error. The rest of the app is still working — you can try
          again, head back to the dashboard, or reload.
        </p>
        {import.meta.env.DEV && (
          <pre className="text-left bg-muted text-xs rounded p-2 overflow-auto max-h-48 mb-4 whitespace-pre-wrap break-words">
            {scope ? `[${scope}] ` : ""}
            {message}
          </pre>
        )}
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            data-testid="route-error-try-again"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
            data-testid="route-error-reload"
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
}
